export type ConsoleLogLevel = "log" | "info" | "warn" | "error";

export type ConsoleLogArg =
  | { type: "string"; value: string }
  | { type: "number"; value: number }
  | { type: "boolean"; value: boolean }
  | { type: "null" }
  | { type: "undefined" }
  | { type: "error"; name: string; message: string; stack?: string }
  | { type: "json"; value: unknown }
  | { type: "unknown"; value: string };

export interface ConsoleLogEntry {
  level: ConsoleLogLevel;
  timestamp: string;
  args: ConsoleLogArg[];
}

let initialized = false;
let maxEntries = 50;

const buffer: ConsoleLogEntry[] = [];

const original: Partial<Record<ConsoleLogLevel, (...args: unknown[]) => void>> = {};

function toSerializableArg(arg: unknown): ConsoleLogArg {
  if (arg === null) return { type: "null" };
  if (arg === undefined) return { type: "undefined" };

  if (arg instanceof Error) {
    return {
      type: "error",
      name: arg.name,
      message: arg.message,
      stack: arg.stack,
    };
  }

  const t = typeof arg;
  if (t === "string") return { type: "string", value: arg as string };
  if (t === "number") return { type: "number", value: arg as number };
  if (t === "boolean") return { type: "boolean", value: arg as boolean };

  // Best-effort JSON serialization for objects
  if (t === "object") {
    try {
      // structuredClone is too strict for some objects; JSON keeps it simple.
      return { type: "json", value: JSON.parse(JSON.stringify(arg)) };
    } catch {
      return { type: "unknown", value: Object.prototype.toString.call(arg) };
    }
  }

  try {
    return { type: "unknown", value: String(arg) };
  } catch {
    return { type: "unknown", value: "[unstringifiable]" };
  }
}

function pushEntry(level: ConsoleLogLevel, args: unknown[]) {
  buffer.unshift({
    level,
    timestamp: new Date().toISOString(),
    args: args.map(toSerializableArg),
  });

  if (buffer.length > maxEntries) {
    buffer.length = maxEntries;
  }
}

export function initConsoleCapture(options?: { maxEntries?: number }) {
  if (initialized) return;
  initialized = true;

  if (options?.maxEntries && options.maxEntries > 0) {
    maxEntries = Math.min(Math.floor(options.maxEntries), 200);
  }

  const wrap = (level: ConsoleLogLevel) => {
    const fn = console[level] as unknown as (...args: unknown[]) => void;
    original[level] = fn;

    console[level] = ((...args: unknown[]) => {
      try {
        pushEntry(level, args);
      } catch {
        // Never block normal console behavior
      }
      fn(...args);
    }) as typeof console[ConsoleLogLevel];
  };

  wrap("log");
  wrap("info");
  wrap("warn");
  wrap("error");
}

export function getConsoleLogs(): ConsoleLogEntry[] {
  // Return a copy so callers can’t mutate internal state
  return buffer.slice(0, maxEntries);
}

export function clearConsoleLogs() {
  buffer.length = 0;
}

export function restoreConsole() {
  (Object.keys(original) as ConsoleLogLevel[]).forEach((level) => {
    const fn = original[level];
    if (fn) {
      console[level] = fn as typeof console[ConsoleLogLevel];
    }
  });
  initialized = false;
}
